/**
 * WeeklyAgenda - Agenda hebdomadaire mobile-first
 * Vue jour unique sur mobile avec swipe, vue grille sur desktop
 * Toutes opérations CRUD optimisées tactile
 */

import React, { memo, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Plus, Trash2,
  Copy, Edit3, X, Zap, Calendar, Check, MoreVertical,
  Cloud, CloudOff, Loader2, Sparkles
} from 'lucide-react';
import { BottomSheet } from '../ui/BottomSheet';
import { WeekGeneratorSheet } from './WeekGeneratorSheet';
import { ProfileSetupSheet } from './ProfileSetupSheet';
import { CompanionWidget } from './CompanionWidget';
import { QuestPanel } from './QuestPanel';
import { MobileWeekCarousel } from './MobileWeekCarousel';
import { useAIWeekGenerator } from '../../hooks/useAIWeekGenerator';

// Configuration
const HOUR_START = 0;
const HOUR_END = 23;
const HOUR_HEIGHT = 60; // Full 24h coverage, slightly compact
const DAYS_SHORT = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const DAYS_FULL = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

// Catégories avec couleurs (opacité réduite pour meilleure lisibilité)
const CATEGORIES = [
  { id: 'vital', name: 'Vital', color: '#EF4444', bg: 'rgba(239,68,68,0.08)', icon: '❤️' },
  { id: 'work', name: 'Travail', color: '#3B82F6', bg: 'rgba(59,130,246,0.08)', icon: '💼' },
  { id: 'health', name: 'Santé', color: '#10B981', bg: 'rgba(16,185,129,0.08)', icon: '🏃' },
  { id: 'personal', name: 'Personnel', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', icon: '🏠' },
  { id: 'social', name: 'Social', color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)', icon: '👥' },
  { id: 'admin', name: 'Admin', color: '#06B6D4', bg: 'rgba(6,182,212,0.08)', icon: '📋' },
];

// Templates rapides
const QUICK_TEMPLATES = [
  { name: 'Réunion', duration: 60, category: 'work', icon: '👥' },
  { name: 'Focus', duration: 90, category: 'work', icon: '🎯' },
  { name: 'Pause', duration: 30, category: 'personal', icon: '☕' },
  { name: 'Sport', duration: 60, category: 'health', icon: '🏃' },
  { name: 'Repas', duration: 45, category: 'personal', icon: '🍽️' },
];

// Utils
const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const formatDateKey = (d) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const isSameDay = (d1, d2) => {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
};

const isToday = (d) => isSameDay(d, new Date());

const timeToMinutes = (time) => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const minutesToTime = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const roundMinutes = (mins, step = 15) => Math.round(mins / step) * step;

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

/**
 * Main WeeklyAgenda Component - Mobile First
 */
export const WeeklyAgenda = memo(function WeeklyAgenda({
  events = [],
  onEventsChange,
  onDeleteEvent,
  onEventClick,
  onSetupRequest,
  onSetupSkip,
  showSetupPrompt = false,
  gameification,
  mood,
  onEventComplete,
  onCelebrate, // Callback pour déclencher la célébration
  // Firebase sync status
  isSyncing = false,
  syncError = null,
  isOnline = false,
}) {
  // États principaux
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [undoStack, setUndoStack] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  // États UI mobile
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showContextMenu, setShowContextMenu] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [mobileViewMode, setMobileViewMode] = useState('day'); // 'day' | 'week'

  // États pour drag-to-create sur mobile
  const [dragCreate, setDragCreate] = useState(null); // { startY, startHour, currentHour }
  const [isDraggingCreate, setIsDraggingCreate] = useState(false);

  // États pour drag-to-move (déplacer un événement existant)
  const [dragMove, setDragMove] = useState(null); // { event, startY, originalStart, originalEnd, currentStart }
  const [isDraggingMove, setIsDraggingMove] = useState(false);

  // États pour génération IA
  const [showWeekGenerator, setShowWeekGenerator] = useState(false);
  const [showProfileSetup, setShowProfileSetup] = useState(false);

  // Hook génération IA avec compagnons et quêtes
  const {
    profile,
    isLoading: isAIGenerating,
    error: aiError,
    isProfileComplete,
    saveProfile,
    generateWeek,
    // Compagnon
    companion,
    companions,
    companionMessage,
    selectCompanion,
    getCompanionMessage,
    // Quêtes
    activeQuests,
    updateQuestProgress,
  } = useAIWeekGenerator();

  // État pour afficher/masquer le panneau IA
  const [showAIPanel, setShowAIPanel] = useState(false);

  // Détection mobile
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024); // Tablettes incluses

  // Refs — scrollRef persists position per week
  const weekKey = weekStart.toISOString().slice(0, 10);
  const scrollRef = useScrollPosition('weekly-agenda', weekKey);
  const timelineRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);

  // Timer mise à jour heure
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Listener resize - tablettes incluses dans vue mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Week dates
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  }, [weekStart]);

  // Events for selected date (mobile) or full week (desktop)
  const todayEvents = useMemo(() => {
    const dateKey = formatDateKey(selectedDate);
    return events.filter(e => e.date === dateKey).sort((a, b) =>
      timeToMinutes(a.start) - timeToMinutes(b.start)
    );
  }, [events, selectedDate]);

  // Full 24h time range — always show the complete day
  const timeRange = useMemo(() => {
    return { start: HOUR_START, end: HOUR_END };
  }, []);

  // Navigation
  const goToDate = useCallback((date) => {
    setSelectedDate(date);
    const newWeekStart = getMonday(date);
    if (newWeekStart.getTime() !== weekStart.getTime()) {
      setWeekStart(newWeekStart);
    }
  }, [weekStart]);

  const navigateDay = useCallback((direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    goToDate(newDate);
  }, [selectedDate, goToDate]);

  const goToday = useCallback(() => {
    goToDate(new Date());
  }, [goToDate]);

  // Event operations
  const pushUndo = useCallback(() => {
    setUndoStack(prev => [...prev.slice(-29), JSON.stringify(events)]);
  }, [events]);

  const addEvent = useCallback((event) => {
    pushUndo();
    const newEvent = { ...event, id: uid(), subTasks: event.subTasks || [] };
    onEventsChange?.([...events, newEvent]);
    return newEvent;
  }, [events, onEventsChange, pushUndo]);

  const updateEvent = useCallback((id, updates) => {
    pushUndo();
    onEventsChange?.(events.map(e => e.id === id ? { ...e, ...updates } : e));
  }, [events, onEventsChange, pushUndo]);

  const deleteEvent = useCallback((id) => {
    pushUndo();
    if (onDeleteEvent) {
      onDeleteEvent(id);
    } else {
      onEventsChange?.(events.filter(e => e.id !== id));
    }
    setShowContextMenu(null);
    setShowEditSheet(false);
  }, [events, onEventsChange, onDeleteEvent, pushUndo]);

  // État pour confirmation de vidage
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Vider la semaine (tous les événements de la semaine en cours)
  const clearWeek = useCallback(() => {
    const weekDateKeys = weekDates.map(d => formatDateKey(d));
    const eventsToRemove = events.filter(e => weekDateKeys.includes(e.date));

    if (eventsToRemove.length === 0) {
      onCelebrate?.("La semaine est déjà vide ! 📭", 'info', false);
      setShowClearConfirm(false);
      return;
    }

    pushUndo();
    const remainingEvents = events.filter(e => !weekDateKeys.includes(e.date));
    onEventsChange?.(remainingEvents);
    setShowClearConfirm(false);

    onCelebrate?.(`🗑️ ${eventsToRemove.length} événement(s) supprimé(s) de la semaine`, 'info', false);
  }, [events, weekDates, onEventsChange, pushUndo, onCelebrate]);

  // Compter les événements de la semaine
  const weekEventCount = useMemo(() => {
    const weekDateKeys = weekDates.map(d => formatDateKey(d));
    const count = events.filter(e => weekDateKeys.includes(e.date)).length;
    // Debug: afficher le nombre d'événements
    console.log('📅 WeeklyAgenda - Total events:', events.length, '| Week events:', count, '| Week dates:', weekDateKeys);
    return count;
  }, [events, weekDates]);

  // Handler pour la génération IA
  const handleAIGenerate = useCallback(async (options) => {
    try {
      const generatedEvents = await generateWeek(options);
      if (generatedEvents && generatedEvents.length > 0) {
        pushUndo();
        // Ajouter les événements générés SANS dupliquer les existants
        const existingKeys = new Set(events.map(e =>
          `${e.date}_${(e.title || e.task || '').toLowerCase()}_${e.start}`
        ));
        const uniqueNew = generatedEvents.filter(e => {
          const key = `${e.date}_${(e.title || e.task || '').toLowerCase()}_${e.start}`;
          return !existingKeys.has(key);
        });
        const newEvents = [...events, ...uniqueNew];
        onEventsChange?.(newEvents);
        setShowWeekGenerator(false);

        // Feedback positif ADHD-friendly
        if (gameification?.addXP) {
          gameification.addXP(50); // Bonus pour avoir planifié sa semaine
        }

        // Célébration avec confetti et message encourageant
        const encouragingMessages = [
          `🎉 ${generatedEvents.length} activités ajoutées ! Ta semaine est prête !`,
          `✨ Planning généré ! Tu es sur la bonne voie !`,
          `🌟 Super ! ${generatedEvents.length} événements créés pour toi !`,
          `💪 Ta semaine est planifiée ! Tu vas assurer !`,
        ];
        const randomMessage = encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)];
        onCelebrate?.(randomMessage, 'success', true);
      } else {
        // Pas d'événements générés - message d'encouragement quand même
        onCelebrate?.("Hmm, pas d'activités générées. Réessaie avec d'autres options ! 🤔", 'info', false);
      }
      return generatedEvents;
    } catch (error) {
      console.error('Erreur génération IA:', error);
      onCelebrate?.("Oups ! Une erreur s'est produite. On réessaie ? 💪", 'error', false);
      return [];
    }
  }, [generateWeek, events, onEventsChange, pushUndo, gameification, onCelebrate]);

  // Ouvrir le générateur ou le profil
  const handleOpenGenerator = useCallback(() => {
    if (!isProfileComplete) {
      setShowProfileSetup(true);
    } else {
      setShowWeekGenerator(true);
    }
  }, [isProfileComplete]);

  const duplicateEvent = useCallback((event) => {
    const duration = timeToMinutes(event.end) - timeToMinutes(event.start);
    addEvent({
      ...event,
      start: event.end,
      end: minutesToTime(timeToMinutes(event.end) + duration),
    });
    setShowContextMenu(null);
  }, [addEvent]);

  // Quick add parsing
  const parseQuickAdd = useCallback((text) => {
    const lower = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    let date = formatDateKey(selectedDate);
    let start = null;
    let duration = 60;

    // Time parsing
    const timeMatch = lower.match(/(\d{1,2})[h:](\d{2})?/);
    if (timeMatch) {
      start = minutesToTime(parseInt(timeMatch[1]) * 60 + parseInt(timeMatch[2] || 0));
    }

    // Duration
    const durMatch = lower.match(/(\d+)\s*min/) || lower.match(/(\d+)h(\d+)?/);
    if (durMatch) {
      duration = durMatch[0].includes('min')
        ? parseInt(durMatch[1])
        : parseInt(durMatch[1]) * 60 + parseInt(durMatch[2] || 0);
    }

    if (!start) {
      const now = new Date();
      start = minutesToTime(roundMinutes(now.getHours() * 60 + now.getMinutes() + 15));
    }
    const end = minutesToTime(timeToMinutes(start) + duration);

    // Category detection
    let category = 'personal';
    if (/reun|meeting|call|appel/.test(lower)) category = 'social';
    else if (/sport|gym|yoga|course/.test(lower)) category = 'health';
    else if (/medic|docteur|rdv/.test(lower)) category = 'vital';
    else if (/travail|projet|code|dev|focus/.test(lower)) category = 'work';

    // Clean title
    let title = text
      .replace(/\d{1,2}[h:]\d{0,2}/g, '')
      .replace(/\d+\s*min/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!title) title = 'Événement';

    return { title, date, start, end, category, subTasks: [] };
  }, [selectedDate]);

  // Touch handlers for swipe navigation (only when not dragging to create)
  const handleTouchStart = useCallback((e) => {
    // Don't track if we're creating an event
    if (isDraggingCreate) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, [isDraggingCreate]);

  const handleTouchEnd = useCallback((e) => {
    // Don't navigate if we were creating an event
    if (isDraggingCreate || dragCreate) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = touchStartX.current - touchEndX;
    const diffY = Math.abs(touchStartY.current - touchEndY);

    // Horizontal swipe (not vertical scroll)
    if (Math.abs(diffX) > 50 && diffY < 50) {
      if (diffX > 0) {
        navigateDay(1); // Swipe left = next day
      } else {
        navigateDay(-1); // Swipe right = previous day
      }
    }
  }, [navigateDay, isDraggingCreate, dragCreate]);

  // Long press for context menu OR drag-to-move
  const handleEventTouchStart = useCallback((event, e) => {
    const touch = e.touches[0];
    touchStartY.current = touch.clientY;
    touchStartTime.current = Date.now();

    // Préparer le drag-to-move
    const originalStartMins = timeToMinutes(event.start);
    const originalEndMins = timeToMinutes(event.end);

    setDragMove({
      event,
      startClientY: touch.clientY,
      originalStart: originalStartMins,
      originalEnd: originalEndMins,
      currentStart: originalStartMins,
      duration: originalEndMins - originalStartMins,
    });

    // Timer pour context menu (long press)
    const timer = setTimeout(() => {
      // Ne pas ouvrir le context menu si on est en train de drag
      if (!isDraggingMove) {
        setShowContextMenu({ event, x: touch.clientX, y: touch.clientY });
        setDragMove(null);
      }
    }, 600);
    setLongPressTimer(timer);
  }, [isDraggingMove]);

  const handleEventTouchMove = useCallback((e) => {
    if (!dragMove) return;

    const touch = e.touches[0];
    const deltaY = touch.clientY - dragMove.startClientY;
    const deltaMinutes = Math.round((deltaY / HOUR_HEIGHT) * 60);

    // Commencer le drag si mouvement > 10px
    if (Math.abs(deltaY) > 10) {
      e.preventDefault();
      e.stopPropagation();

      // Annuler le timer du context menu
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }

      setIsDraggingMove(true);

      // Calculer la nouvelle position (arrondie à 15 min)
      const newStart = roundMinutes(dragMove.originalStart + deltaMinutes, 15);
      const clampedStart = Math.max(HOUR_START * 60, Math.min(newStart, (HOUR_END * 60) - dragMove.duration));

      setDragMove(prev => ({
        ...prev,
        currentStart: clampedStart,
      }));
    }
  }, [dragMove, longPressTimer]);

  const handleEventTouchEnd = useCallback((e) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    // Si on était en train de drag, sauvegarder la nouvelle position
    if (isDraggingMove && dragMove) {
      e.preventDefault();
      e.stopPropagation();

      const newStart = minutesToTime(dragMove.currentStart);
      const newEnd = minutesToTime(dragMove.currentStart + dragMove.duration);

      // Mettre à jour l'événement
      updateEvent(dragMove.event.id, {
        start: newStart,
        end: newEnd,
      });
    }

    setDragMove(null);
    setIsDraggingMove(false);
  }, [longPressTimer, isDraggingMove, dragMove, updateEvent]);

  // Open edit sheet — delegates to parent if onEventClick is provided
  const openEditSheet = useCallback((event) => {
    setShowContextMenu(null);
    if (onEventClick) {
      onEventClick(event);
    } else {
      setEditingEvent(event);
      setShowEditSheet(true);
    }
  }, [onEventClick]);

  // Create new event from tap on timeline
  const handleTimelineTap = useCallback((hour) => {
    const start = minutesToTime(hour * 60);
    const end = minutesToTime(hour * 60 + 60);
    setEditingEvent({
      title: '',
      date: formatDateKey(selectedDate),
      start,
      end,
      category: 'personal',
      subTasks: [],
    });
    setShowEditSheet(true);
  }, [selectedDate]);

  // Touch handlers for drag-to-create on mobile timeline
  const getMinutesFromTouch = useCallback((touchY) => {
    if (!timelineRef.current) return timeRange.start * 60;
    const rect = timelineRef.current.getBoundingClientRect();
    const scrollTop = scrollRef.current?.scrollTop || 0;
    const relativeY = touchY - rect.top + scrollTop;
    const minutes = (relativeY / HOUR_HEIGHT) * 60 + timeRange.start * 60;
    return roundMinutes(Math.max(HOUR_START * 60, Math.min(minutes, HOUR_END * 60)), 15);
  }, [timeRange.start]);

  const handleTimelineTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    const timelineRect = timelineRef.current?.getBoundingClientRect();
    const touchX = touch.clientX - (timelineRect?.left || 0);

    // La zone des heures (48px à gauche) permet toujours de créer
    const isInHourColumn = touchX < 48;

    // Ignore if touching an event card (sauf si dans la zone des heures)
    if (!isInHourColumn && e.target.closest('.event-card')) return;

    const startMinutes = getMinutesFromTouch(touch.clientY);

    touchStartTime.current = Date.now();
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;

    // Stop propagation to prevent parent swipe handler
    e.stopPropagation();

    setDragCreate({
      startMinutes,
      currentMinutes: startMinutes + 30,
      startClientY: touch.clientY,
    });
  }, [getMinutesFromTouch]);

  const handleTimelineTouchMove = useCallback((e) => {
    if (!dragCreate) return;

    const touch = e.touches[0];
    const deltaY = Math.abs(touch.clientY - dragCreate.startClientY);
    const deltaX = Math.abs(touch.clientX - touchStartX.current);

    // Only start drag-to-create if moving vertically more than 10px
    if (deltaY > 10) {
      e.preventDefault(); // Prevent scroll
      e.stopPropagation(); // Prevent parent handlers
      setIsDraggingCreate(true);

      const currentMinutes = getMinutesFromTouch(touch.clientY);
      setDragCreate(prev => ({
        ...prev,
        currentMinutes,
      }));
    }
  }, [dragCreate, getMinutesFromTouch]);

  const handleTimelineTouchEnd = useCallback((e) => {
    // Always stop propagation to prevent parent swipe handler
    e.stopPropagation();

    if (!dragCreate) return;

    const touchDuration = Date.now() - touchStartTime.current;

    // Create event if dragged significantly or long pressed
    if (isDraggingCreate || touchDuration > 300) {
      e.preventDefault();

      const startMin = Math.min(dragCreate.startMinutes, dragCreate.currentMinutes);
      const endMin = Math.max(dragCreate.startMinutes, dragCreate.currentMinutes);

      setEditingEvent({
        title: '',
        date: formatDateKey(selectedDate),
        start: minutesToTime(startMin),
        end: minutesToTime(Math.max(endMin, startMin + 30)),
        category: 'personal',
        subTasks: [],
      });
      setShowEditSheet(true);
    }

    setDragCreate(null);
    setIsDraggingCreate(false);
  }, [dragCreate, isDraggingCreate, selectedDate]);

  // Quick add for a specific day (from day pill long press)
  const handleDayQuickAdd = useCallback((date) => {
    const now = new Date();
    const start = minutesToTime(roundMinutes(now.getHours() * 60 + now.getMinutes() + 15));
    const end = minutesToTime(timeToMinutes(start) + 60);
    setEditingEvent({
      title: '',
      date: formatDateKey(date),
      start,
      end,
      category: 'personal',
      subTasks: [],
    });
    setSelectedDate(date);
    setShowEditSheet(true);
  }, []);

  // Apply template
  const applyTemplate = useCallback((template) => {
    const now = new Date();
    const start = minutesToTime(roundMinutes(now.getHours() * 60 + now.getMinutes() + 15));
    const end = minutesToTime(timeToMinutes(start) + template.duration);
    addEvent({
      title: template.name,
      date: formatDateKey(selectedDate),
      start,
      end,
      category: template.category,
      subTasks: [],
    });
    setShowAddSheet(false);
  }, [addEvent, selectedDate]);

  // Week label
  const weekLabel = useMemo(() => {
    const start = weekDates[0];
    const end = weekDates[6];
    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()} - ${end.getDate()} ${MONTHS[start.getMonth()]}`;
    }
    return `${start.getDate()} ${MONTHS[start.getMonth()].slice(0, 3)} - ${end.getDate()} ${MONTHS[end.getMonth()].slice(0, 3)}`;
  }, [weekDates]);

  // Current hour indicator position (using smart range)
  const nowPosition = useMemo(() => {
    if (!isToday(selectedDate)) return null;
    const mins = currentTime.getHours() * 60 + currentTime.getMinutes();
    if (mins < timeRange.start * 60 || mins > timeRange.end * 60) return null;
    return ((mins - timeRange.start * 60) / 60) * HOUR_HEIGHT;
  }, [selectedDate, currentTime, timeRange]);

  // Current/next event detection
  const { currentEvent, nextEvent, timeUntilNext } = useMemo(() => {
    if (!isToday(selectedDate)) return { currentEvent: null, nextEvent: null, timeUntilNext: null };
    const nowMins = currentTime.getHours() * 60 + currentTime.getMinutes();

    let current = null, next = null;
    const sorted = [...todayEvents].sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));

    for (const e of sorted) {
      const start = timeToMinutes(e.start);
      const end = timeToMinutes(e.end);
      if (nowMins >= start && nowMins < end) {
        current = e;
      } else if (nowMins < start && !next) {
        next = e;
      }
    }

    const untilNext = next ? timeToMinutes(next.start) - nowMins : null;
    return { currentEvent: current, nextEvent: next, timeUntilNext: untilNext };
  }, [todayEvents, currentTime, selectedDate]);

  // Calculate overlapping events for layout
  const eventsWithLayout = useMemo(() => {
    const sorted = [...todayEvents].sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));
    const result = [];
    const columns = []; // Track end times for each column

    sorted.forEach(event => {
      const startMin = timeToMinutes(event.start);
      const endMin = timeToMinutes(event.end);

      // Find first available column
      let col = 0;
      while (columns[col] && columns[col] > startMin) {
        col++;
      }
      columns[col] = endMin;

      // Count total columns needed for this group
      const activeColumns = columns.filter(end => end > startMin).length;

      result.push({
        ...event,
        column: col,
        totalColumns: Math.max(activeColumns, col + 1),
      });
    });

    // Second pass: update totalColumns for overlapping events
    return result.map(event => {
      const startMin = timeToMinutes(event.start);
      const endMin = timeToMinutes(event.end);
      const overlapping = result.filter(e => {
        const eStart = timeToMinutes(e.start);
        const eEnd = timeToMinutes(e.end);
        return !(eEnd <= startMin || eStart >= endMin);
      });
      return {
        ...event,
        totalColumns: Math.max(...overlapping.map(e => e.column + 1)),
      };
    });
  }, [todayEvents]);

  // Day stats
  const dayStats = useMemo(() => {
    const totalMinutes = todayEvents.reduce((sum, e) => {
      return sum + (timeToMinutes(e.end) - timeToMinutes(e.start));
    }, 0);
    const completedCount = todayEvents.filter(e => e.completed).length;
    const totalSubtasks = todayEvents.reduce((sum, e) => sum + (e.subTasks?.length || 0), 0);
    const completedSubtasks = todayEvents.reduce((sum, e) =>
      sum + (e.subTasks?.filter(st => st.completed).length || 0), 0);

    return {
      totalHours: Math.round(totalMinutes / 60 * 10) / 10,
      totalMinutes,
      completedEvents: completedCount,
      totalEvents: todayEvents.length,
      completedSubtasks,
      totalSubtasks,
      progressPercent: totalSubtasks > 0 ? Math.round(completedSubtasks / totalSubtasks * 100) : 0,
    };
  }, [todayEvents]);

  // ============ MOBILE VIEW ============
  if (isMobile) {
    return (
      <div className="flex flex-col h-full bg-zinc-950">
        {/* Header Navigation */}
        <div className="flex-shrink-0 bg-zinc-900/80 backdrop-blur-sm border-b border-zinc-800">
          {/* Week navigation with sync indicator */}
          <div className="flex items-center justify-between px-4 py-2">
            <button
              onClick={() => navigateDay(-7)}
              className="p-2 -ml-2 text-zinc-400 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={goToday}
                className="text-sm font-medium text-zinc-300"
              >
                {weekLabel}
              </button>

              {/* View mode toggle */}
              <div className="flex items-center bg-zinc-800 rounded-lg p-0.5">
                <button
                  onClick={() => setMobileViewMode('day')}
                  className={`px-2 py-1 text-[10px] font-semibold rounded transition-colors ${
                    mobileViewMode === 'day'
                      ? 'bg-blue-600 text-white'
                      : 'text-zinc-400 hover:text-zinc-300'
                  }`}
                >
                  Jour
                </button>
                <button
                  onClick={() => setMobileViewMode('week')}
                  className={`px-2 py-1 text-[10px] font-semibold rounded transition-colors ${
                    mobileViewMode === 'week'
                      ? 'bg-blue-600 text-white'
                      : 'text-zinc-400 hover:text-zinc-300'
                  }`}
                >
                  Semaine
                </button>
              </div>

              {/* Sync indicator */}
              <div className="flex items-center">
                {isSyncing ? (
                  <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                ) : syncError ? (
                  <CloudOff className="w-4 h-4 text-red-400" title={syncError} />
                ) : isOnline ? (
                  <Cloud className="w-4 h-4 text-emerald-400" />
                ) : (
                  <CloudOff className="w-4 h-4 text-zinc-600" />
                )}
              </div>
            </div>

            <button
              onClick={() => navigateDay(7)}
              className="p-2 -mr-2 text-zinc-400 hover:text-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Day pills with quick add */}
          <div className="flex gap-1 px-2 pb-2 overflow-x-auto scrollbar-hide">
            {weekDates.map((date) => {
              const isSelected = isSameDay(date, selectedDate);
              const isTodayDate = isToday(date);
              const dayEvents = events.filter(e => e.date === formatDateKey(date));

              return (
                <div key={formatDateKey(date)} className="flex-shrink-0 relative">
                  <button
                    onClick={() => setSelectedDate(date)}
                    className={`flex flex-col items-center px-3 py-2 rounded-xl transition-all ${
                      isSelected
                        ? 'bg-blue-600 text-white'
                        : isTodayDate
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'text-zinc-400 hover:bg-zinc-800'
                    }`}
                  >
                    <span className="text-[10px] font-bold uppercase">
                      {DAYS_SHORT[date.getDay()]}
                    </span>
                    <span className={`text-lg font-bold ${isSelected ? 'text-white' : ''}`}>
                      {date.getDate()}
                    </span>
                    {dayEvents.length > 0 ? (
                      <div className="flex gap-0.5 mt-0.5">
                        {dayEvents.slice(0, 3).map((_, i) => (
                          <div
                            key={i}
                            className={`w-1 h-1 rounded-full ${isSelected ? 'bg-white/60' : 'bg-blue-500'}`}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="h-2" />
                    )}
                  </button>
                  {/* Quick add button on selected day */}
                  {isSelected && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDayQuickAdd(date);
                      }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30"
                    >
                      <Plus className="w-3 h-3 text-white" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Week Carousel Mode */}
        {mobileViewMode === 'week' && (
          <div className="flex-1 overflow-auto p-4">
            <MobileWeekCarousel
              weekDates={weekDates}
              events={events}
              selectedDate={selectedDate}
              onSelectDate={(date) => {
                setSelectedDate(date);
                setMobileViewMode('day'); // Switch to day view when selecting a day
              }}
              onEventTap={(event) => {
                if (onEventClick) {
                  onEventClick(event);
                } else {
                  setEditingEvent(event);
                  setShowEditSheet(true);
                }
              }}
              onAddTap={(date) => {
                setSelectedDate(date);
                setShowAddSheet(true);
              }}
            />

            {/* Weekly summary */}
            <div className="mt-4 p-4 bg-zinc-800/30 rounded-xl border border-zinc-700/30">
              <h3 className="text-sm font-semibold text-zinc-300 mb-3">Résumé de la semaine</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-zinc-800/50 rounded-lg">
                  <div className="text-xl font-bold text-white">
                    {events.filter(e => weekDates.some(d => formatDateKey(d) === e.date)).length}
                  </div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Événements</div>
                </div>
                <div className="text-center p-2 bg-zinc-800/50 rounded-lg">
                  <div className="text-xl font-bold text-emerald-400">
                    {events.filter(e => {
                      const inWeek = weekDates.some(d => formatDateKey(d) === e.date);
                      const isComplete = e.completed || (e.subTasks?.length > 0 && e.subTasks.every(st => st.completed));
                      return inWeek && isComplete;
                    }).length}
                  </div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Terminés</div>
                </div>
                <div className="text-center p-2 bg-zinc-800/50 rounded-lg">
                  <div className="text-xl font-bold text-amber-400">
                    {(() => {
                      const weekEvents = events.filter(e => weekDates.some(d => formatDateKey(d) === e.date));
                      const totalMins = weekEvents.reduce((sum, e) => {
                        return sum + (timeToMinutes(e.end) - timeToMinutes(e.start));
                      }, 0);
                      return Math.round(totalMins / 60);
                    })()}h
                  </div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Planifiées</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Day View Mode */}
        {mobileViewMode === 'day' && (
          <>
        {/* Enhanced Day Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-zinc-900 to-zinc-900/50 border-b border-zinc-800/50">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                {DAYS_FULL[selectedDate.getDay()]}
                {isToday(selectedDate) && (
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-semibold rounded-full">
                    Aujourd'hui
                  </span>
                )}
              </h2>
              <p className="text-sm text-zinc-400 mt-0.5">
                {selectedDate.getDate()} {MONTHS[selectedDate.getMonth()]}
              </p>
            </div>

            {/* Day Stats */}
            <div className="flex gap-3">
              <div className="text-center">
                <div className="text-lg font-bold text-white">{dayStats.totalEvents}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Tâches</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-emerald-400">{dayStats.totalHours}h</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Planifié</div>
              </div>
              {dayStats.totalSubtasks > 0 && (
                <div className="text-center">
                  <div className="text-lg font-bold text-amber-400">{dayStats.progressPercent}%</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Fait</div>
                </div>
              )}
            </div>
          </div>

          {/* Current/Next Event Banner */}
          {isToday(selectedDate) && (currentEvent || nextEvent) && (
            <div className="mt-3 p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
              {currentEvent ? (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] uppercase tracking-wider text-green-400 font-semibold">En cours</span>
                    <p className="text-sm font-medium text-white truncate">{currentEvent.title}</p>
                  </div>
                  <div className="text-xs text-zinc-400">
                    jusqu'à {currentEvent.end}
                  </div>
                </div>
              ) : nextEvent && (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] uppercase tracking-wider text-blue-400 font-semibold">
                      Prochaine dans {timeUntilNext < 60 ? `${timeUntilNext}min` : `${Math.floor(timeUntilNext / 60)}h${timeUntilNext % 60 > 0 ? timeUntilNext % 60 : ''}`}
                    </span>
                    <p className="text-sm font-medium text-white truncate">{nextEvent.title}</p>
                  </div>
                  <div className="text-xs text-zinc-400">
                    {nextEvent.start}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Companion & Quests Panel - Collapsible */}
          {companion && (
            <div className="mt-3">
              <button
                onClick={() => setShowAIPanel(!showAIPanel)}
                className="w-full flex items-center justify-between p-2 bg-zinc-800/30 rounded-lg hover:bg-zinc-800/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{companion.emoji}</span>
                  <span className="text-sm text-zinc-300">{companion.name}</span>
                  {activeQuests.length > 0 && (
                    <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                      {activeQuests.length} quêtes
                    </span>
                  )}
                </div>
                <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${showAIPanel ? 'rotate-90' : ''}`} />
              </button>

              <AnimatePresence>
                {showAIPanel && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-3 space-y-3">
                      {/* Compagnon */}
                      <CompanionWidget
                        companion={companion}
                        companions={companions}
                        message={companionMessage}
                        onSelectCompanion={selectCompanion}
                        onRequestMessage={getCompanionMessage}
                        energy={3}
                      />

                      {/* Quêtes */}
                      {activeQuests.length > 0 && (
                        <QuestPanel
                          quests={activeQuests}
                          onQuestClick={(quest) => console.log('Quest clicked:', quest)}
                        />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Timeline with drag-to-create */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-auto"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            ref={timelineRef}
            className="relative"
            style={{
              minHeight: (timeRange.end - timeRange.start + 1) * HOUR_HEIGHT,
              touchAction: 'pan-x',
            }}
            onTouchStart={handleTimelineTouchStart}
            onTouchMove={handleTimelineTouchMove}
            onTouchEnd={handleTimelineTouchEnd}
          >
            {/* Hour lines - using smart range */}
            {Array.from({ length: timeRange.end - timeRange.start + 1 }, (_, i) => {
              const hour = timeRange.start + i;
              const isCurrentHour = isToday(selectedDate) && currentTime.getHours() === hour;

              return (
                <div
                  key={hour}
                  className={`absolute left-0 right-0 flex border-t ${
                    isCurrentHour ? 'border-blue-500/30 bg-blue-500/5' : 'border-zinc-800/50'
                  }`}
                  style={{ top: i * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                >
                  <div className="w-12 flex-shrink-0 pr-2 pt-1 text-right">
                    <span className={`text-[10px] font-mono ${
                      isCurrentHour ? 'text-blue-400 font-bold' : 'text-zinc-600'
                    }`}>
                      {String(hour).padStart(2, '0')}:00
                    </span>
                  </div>
                  {/* Half-hour line */}
                  <div className="flex-1 relative">
                    <div
                      className="absolute left-0 right-0 border-t border-zinc-800/20"
                      style={{ top: HOUR_HEIGHT / 2 }}
                    />
                  </div>
                </div>
              );
            })}

            {/* Drag-to-create preview */}
            {dragCreate && isDraggingCreate && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute left-12 right-2 bg-blue-500/30 border-2 border-dashed border-blue-500 rounded-xl z-40 flex flex-col items-center justify-center backdrop-blur-sm"
                style={{
                  top: ((Math.min(dragCreate.startMinutes, dragCreate.currentMinutes) - timeRange.start * 60) / 60) * HOUR_HEIGHT,
                  height: Math.max(
                    ((Math.abs(dragCreate.currentMinutes - dragCreate.startMinutes)) / 60) * HOUR_HEIGHT,
                    30
                  ),
                }}
              >
                <span className="text-blue-400 font-bold text-sm">
                  {minutesToTime(Math.min(dragCreate.startMinutes, dragCreate.currentMinutes))}
                </span>
                <span className="text-blue-300 text-xs">
                  — {minutesToTime(Math.max(dragCreate.startMinutes, dragCreate.currentMinutes))}
                </span>
                <span className="text-[10px] text-blue-400/60 mt-1">
                  {Math.abs(dragCreate.currentMinutes - dragCreate.startMinutes)}min
                </span>
              </motion.div>
            )}

            {/* Touch hint when starting drag */}
            {dragCreate && !isDraggingCreate && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute left-12 right-2 h-1.5 bg-blue-500 rounded-full z-40 shadow-lg shadow-blue-500/50"
                style={{
                  top: ((dragCreate.startMinutes - timeRange.start * 60) / 60) * HOUR_HEIGHT,
                }}
              />
            )}

            {/* Drag-to-move: indicateur de position originale */}
            {isDraggingMove && dragMove && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute left-12 right-2 border-2 border-dashed border-zinc-500 rounded-xl z-30 pointer-events-none"
                style={{
                  top: ((dragMove.originalStart - timeRange.start * 60) / 60) * HOUR_HEIGHT,
                  height: (dragMove.duration / 60) * HOUR_HEIGHT,
                  backgroundColor: 'rgba(100, 100, 100, 0.1)',
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs text-zinc-500 font-medium">Position originale</span>
                </div>
              </motion.div>
            )}

            {/* Drag-to-move: indicateur de nouvelle position */}
            {isDraggingMove && dragMove && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute left-12 z-45 pointer-events-none flex items-center gap-1"
                style={{
                  top: ((dragMove.currentStart - timeRange.start * 60) / 60) * HOUR_HEIGHT - 20,
                }}
              >
                <div className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded shadow-lg">
                  {minutesToTime(dragMove.currentStart)} → {minutesToTime(dragMove.currentStart + dragMove.duration)}
                </div>
              </motion.div>
            )}

            {/* Enhanced Now indicator */}
            {nowPosition !== null && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute left-0 right-0 z-20 flex items-center pointer-events-none"
                style={{ top: nowPosition }}
              >
                <div className="w-12 flex items-center justify-end pr-1">
                  <span className="text-[9px] font-bold text-red-400 bg-red-500/20 px-1.5 py-0.5 rounded">
                    {String(currentTime.getHours()).padStart(2, '0')}:{String(currentTime.getMinutes()).padStart(2, '0')}
                  </span>
                </div>
                <div className="w-3 h-3 rounded-full bg-red-500 shadow-lg shadow-red-500/50 -ml-1.5 border-2 border-zinc-950" />
                <div className="flex-1 h-0.5 bg-gradient-to-r from-red-500 to-red-500/0" />
              </motion.div>
            )}

            {/* Events with overlap handling - Refined UI/UX */}
            {eventsWithLayout.map((event) => {
              const category = CATEGORIES.find(c => c.id === event.category) || CATEGORIES[3];
              const startMin = timeToMinutes(event.start);
              const endMin = timeToMinutes(event.end);
              const duration = endMin - startMin;
              const top = ((startMin - timeRange.start * 60) / 60) * HOUR_HEIGHT;
              const height = Math.max((duration / 60) * HOUR_HEIGHT, 52); // Min 52px for touch

              // Layout calculations with proper spacing
              const gutter = 48; // 12 * 4 = 48px time column
              const gap = 4; // 4px gap between overlapping events
              const columnWidth = (100 - 13) / event.totalColumns; // 13% = gutter
              const left = 13 + (event.column * columnWidth) + (event.column * 0.3);
              const width = columnWidth - 0.6;

              // State calculations
              const isCurrent = currentEvent?.id === event.id;
              const isPast = isToday(selectedDate) && endMin < currentTime.getHours() * 60 + currentTime.getMinutes();
              const isUpcoming = isToday(selectedDate) && !isCurrent && !isPast && startMin - (currentTime.getHours() * 60 + currentTime.getMinutes()) < 30;
              const completedSubtasks = event.subTasks?.filter(st => st.completed).length || 0;
              const totalSubtasks = event.subTasks?.length || 0;
              const progress = totalSubtasks > 0 ? Math.round(completedSubtasks / totalSubtasks * 100) : 0;
              const isCompleted = event.completed || (totalSubtasks > 0 && completedSubtasks === totalSubtasks);

              // Adaptive layout based on height
              const isCompact = height < 56;
              const isSmall = height >= 56 && height < 80;
              const isMedium = height >= 80 && height < 110;
              const isLarge = height >= 110;

              // Format duration intelligently
              const formatDuration = (mins) => {
                if (mins < 60) return `${mins}'`;
                const h = Math.floor(mins / 60);
                const m = mins % 60;
                return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
              };

              // Si cet événement est en train d'être déplacé, utiliser la nouvelle position
              const isBeingDragged = isDraggingMove && dragMove?.event?.id === event.id;
              const displayTop = isBeingDragged
                ? ((dragMove.currentStart - timeRange.start * 60) / 60) * HOUR_HEIGHT
                : top;

              return (
                <motion.div
                  key={event.id}
                  className={`event-card absolute overflow-hidden ${isBeingDragged ? 'shadow-2xl shadow-blue-500/30 z-50' : ''}`}
                  style={{
                    top: displayTop + 1, // 1px offset for visual separation
                    height: height - 2, // 2px total gap
                    left: `${left}%`,
                    width: `${width}%`,
                    zIndex: isBeingDragged ? 50 : isCurrent ? 30 : isPast ? 5 : 10,
                    opacity: isBeingDragged ? 0.9 : 1,
                    transform: isBeingDragged ? 'scale(1.02)' : undefined,
                  }}
                  onClick={(e) => {
                    // Ne pas ouvrir l'éditeur si on vient de drag
                    if (!isDraggingMove) {
                      openEditSheet(event);
                    }
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleEventTouchStart(event, e);
                  }}
                  onTouchMove={handleEventTouchMove}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    handleEventTouchEnd(e);
                  }}
                  onTouchCancel={(e) => {
                    handleEventTouchEnd(e);
                  }}
                  whileTap={isDraggingMove ? {} : { scale: 0.985 }}
                  animate={isBeingDragged ? { scale: 1.02 } : { scale: 1 }}
                  transition={{ duration: 0.15 }}
                >
                  {/* Card container - flat design, no radius */}
                  <div
                    className={`h-full relative transition-all duration-200 ${
                      isCurrent
                        ? 'ring-2 ring-emerald-400/80 ring-offset-1 ring-offset-zinc-950'
                        : isUpcoming
                          ? 'border-r-2 border-amber-400/50'
                          : isPast || isCompleted
                            ? 'opacity-40'
                            : ''
                    }`}
                    style={{
                      backgroundColor: category.bg,
                      borderLeft: `3px solid ${category.color}`,
                    }}
                  >
                    {/* Progress indicator - top edge */}
                    {totalSubtasks > 0 && (
                      <div className="absolute top-0 left-0 right-0 h-[2px] bg-black/10 overflow-hidden">
                        <motion.div
                          className="h-full"
                          style={{ backgroundColor: progress === 100 ? '#22c55e' : category.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${progress}%` }}
                          transition={{ duration: 0.5, ease: 'easeOut' }}
                        />
                      </div>
                    )}

                    {/* Content container with proper padding scale */}
                    <div className={`h-full flex flex-col ${
                      isCompact ? 'px-2 py-1' : isSmall ? 'px-2.5 py-1.5' : 'px-3 py-2'
                    } ${totalSubtasks > 0 ? 'pt-2' : ''}`}>

                      {/* === COMPACT LAYOUT (< 56px) === */}
                      {isCompact && (
                        <div className="flex items-center gap-2 h-full">
                          <div
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: category.color }}
                          />
                          <span
                            className={`text-[13px] font-semibold truncate flex-1 leading-none ${
                              isPast || isCompleted ? 'line-through opacity-70' : ''
                            }`}
                            style={{ color: category.color }}
                          >
                            {event.title || 'Sans titre'}
                          </span>
                          <span className="text-[10px] text-zinc-500 font-medium tabular-nums">
                            {event.start}
                          </span>
                        </div>
                      )}

                      {/* === SMALL LAYOUT (56-80px) === */}
                      {isSmall && (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <h3
                              className={`text-[13px] font-semibold leading-snug line-clamp-2 ${
                                isPast || isCompleted ? 'line-through opacity-70' : ''
                              }`}
                              style={{ color: category.color }}
                            >
                              {event.title || 'Sans titre'}
                            </h3>
                            {totalSubtasks > 0 && (
                              <div className="flex items-center gap-0.5 text-[10px] text-zinc-400 flex-shrink-0">
                                <Check className="w-3 h-3" />
                                <span className="tabular-nums">{completedSubtasks}/{totalSubtasks}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-auto">
                            <span className="text-[11px] text-zinc-400 font-mono tabular-nums">
                              {event.start}
                            </span>
                            <span
                              className="text-[10px] font-medium px-1.5 py-0.5"
                              style={{
                                backgroundColor: `${category.color}15`,
                                color: category.color,
                              }}
                            >
                              {formatDuration(duration)}
                            </span>
                            {isCurrent && (
                              <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wider animate-pulse">
                                Now
                              </span>
                            )}
                          </div>
                        </>
                      )}

                      {/* === MEDIUM LAYOUT (80-110px) === */}
                      {isMedium && (
                        <>
                          {/* Header */}
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <h3
                                className={`text-[14px] font-semibold leading-snug line-clamp-2 ${
                                  isPast || isCompleted ? 'line-through opacity-70' : ''
                                }`}
                                style={{ color: category.color }}
                              >
                                {event.title || 'Sans titre'}
                              </h3>
                              {/* Time row */}
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[11px] text-zinc-400 font-mono tabular-nums">
                                  {event.start} → {event.end}
                                </span>
                                <span
                                  className="text-[10px] font-semibold px-1.5 py-0.5"
                                  style={{
                                    backgroundColor: `${category.color}15`,
                                    color: category.color,
                                  }}
                                >
                                  {formatDuration(duration)}
                                </span>
                              </div>
                            </div>
                            {isCurrent && (
                              <div className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-lg shadow-emerald-400/50" />
                            )}
                          </div>

                          {/* Subtasks preview */}
                          {totalSubtasks > 0 && (
                            <div className="mt-auto pt-1.5 flex items-center gap-2">
                              <div className="flex-1 h-1 bg-black/15 overflow-hidden">
                                <div
                                  className="h-full transition-all duration-300"
                                  style={{
                                    width: `${progress}%`,
                                    backgroundColor: progress === 100 ? '#22c55e' : category.color,
                                  }}
                                />
                              </div>
                              <span className="text-[10px] text-zinc-400 tabular-nums font-medium">
                                {completedSubtasks}/{totalSubtasks}
                              </span>
                            </div>
                          )}
                        </>
                      )}

                      {/* === LARGE LAYOUT (>= 110px) === */}
                      {isLarge && (
                        <>
                          {/* Header with status */}
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {isCurrent && (
                                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-500/15 text-[9px] font-bold text-emerald-400 uppercase tracking-wide">
                                    <span className="w-1.5 h-1.5 bg-emerald-400 animate-pulse" />
                                    En cours
                                  </span>
                                )}
                                {isUpcoming && (
                                  <span className="px-2 py-0.5 bg-amber-500/15 text-[9px] font-bold text-amber-400 uppercase tracking-wide">
                                    Bientôt
                                  </span>
                                )}
                              </div>
                              <h3
                                className={`text-[15px] font-semibold leading-snug mt-0.5 ${
                                  isPast || isCompleted ? 'line-through opacity-70' : ''
                                }`}
                                style={{ color: category.color }}
                              >
                                {event.title || 'Sans titre'}
                              </h3>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowContextMenu({ event, x: e.clientX, y: e.clientY });
                              }}
                              className="p-1.5 -mr-1 hover:bg-black/20 transition-colors"
                            >
                              <MoreVertical className="w-4 h-4 text-zinc-500" />
                            </button>
                          </div>

                          {/* Time info */}
                          <div className="flex items-center gap-3 mt-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[12px] text-zinc-300 font-mono tabular-nums">
                                {event.start}
                              </span>
                              <span className="text-[11px] text-zinc-600">→</span>
                              <span className="text-[12px] text-zinc-300 font-mono tabular-nums">
                                {event.end}
                              </span>
                            </div>
                            <span
                              className="text-[11px] font-semibold px-2 py-0.5"
                              style={{
                                backgroundColor: `${category.color}15`,
                                color: category.color,
                              }}
                            >
                              {formatDuration(duration)}
                            </span>
                          </div>

                          {/* Subtasks list */}
                          {totalSubtasks > 0 && (
                            <div className="mt-auto pt-2 space-y-1 border-t border-white/5">
                              {event.subTasks.slice(0, height > 140 ? 3 : 2).map((st) => (
                                <div
                                  key={st.id}
                                  className="flex items-center gap-2 group"
                                >
                                  <div
                                    className={`w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center transition-all ${
                                      st.completed
                                        ? 'bg-emerald-500'
                                        : 'border border-zinc-600'
                                    }`}
                                  >
                                    {st.completed && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                                  </div>
                                  <span
                                    className={`text-[12px] truncate leading-tight ${
                                      st.completed
                                        ? 'line-through text-zinc-500'
                                        : 'text-zinc-300'
                                    }`}
                                  >
                                    {st.text}
                                  </span>
                                </div>
                              ))}
                              {totalSubtasks > (height > 140 ? 3 : 2) && (
                                <span className="text-[10px] text-zinc-500 pl-6 font-medium">
                                  +{totalSubtasks - (height > 140 ? 3 : 2)} étapes
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Empty state with instructions */}
            {todayEvents.length === 0 && !isDraggingCreate && (
              <div className="absolute inset-x-0 top-1/4 flex flex-col items-center text-center px-6 pointer-events-none">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-b from-zinc-900 to-zinc-900/95 backdrop-blur-sm rounded-2xl p-6 border border-zinc-800/50 shadow-xl pointer-events-auto max-w-xs"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center border border-blue-500/20">
                    <Calendar className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">Journée libre</h3>
                  <p className="text-zinc-400 text-sm mb-5 leading-relaxed">
                    Aucun événement prévu.<br/>
                    Planifiez votre journée !
                  </p>

                  <div className="space-y-3 text-left">
                    <div className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded-lg">
                      <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-400 text-lg">👆</span>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-white">Glisser-déposer</p>
                        <p className="text-[10px] text-zinc-500">Touchez et glissez pour créer</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded-lg">
                      <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Plus className="w-4 h-4 text-green-400" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-white">Bouton rapide</p>
                        <p className="text-[10px] text-zinc-500">En bas à droite ou sur le jour</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </div>
          </>
        )}

        {/* FABs - AI Generator, Clear Week and Quick Add */}
        <div className="fixed bottom-20 right-4 flex flex-col gap-3 z-40">
          {/* Clear Week FAB (visible seulement si événements) */}
          {weekEventCount > 0 && (
            <motion.button
              onClick={() => setShowClearConfirm(true)}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-10 h-10 rounded-full shadow-lg flex items-center justify-center text-red-400 bg-zinc-800 border border-red-500/30 hover:bg-red-500/20"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Vider la semaine"
            >
              <Trash2 className="w-4 h-4" />
            </motion.button>
          )}

          {/* AI Generator FAB - ADHD friendly with visual cues */}
          <motion.button
            onClick={handleOpenGenerator}
            className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white
              ${isAIGenerating
                ? 'bg-purple-600 animate-pulse shadow-purple-600/30'
                : 'bg-gradient-to-br from-purple-500 to-pink-500 shadow-purple-500/30 hover:shadow-purple-500/50'
              }`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Générer ma semaine avec l'IA"
          >
            {isAIGenerating ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Zap className="w-5 h-5" />
            )}
          </motion.button>

          {/* Quick Add FAB */}
          <motion.button
            onClick={() => setShowAddSheet(true)}
            className="w-14 h-14 bg-blue-600 rounded-full shadow-xl shadow-blue-600/30 flex items-center justify-center text-white"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus className="w-6 h-6" />
          </motion.button>
        </div>

        {/* Clear Week Confirmation Modal */}
        <AnimatePresence>
          {showClearConfirm && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 z-50"
                onClick={() => setShowClearConfirm(false)}
              />
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-zinc-900 border border-zinc-700 rounded-2xl p-6 z-50 w-[90%] max-w-sm text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                  <Trash2 className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Vider la semaine ?</h3>
                <p className="text-sm text-zinc-400 mb-6">
                  Cette action supprimera <span className="text-red-400 font-bold">{weekEventCount} événement(s)</span> de la semaine en cours.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="flex-1 py-3 bg-zinc-800 text-zinc-300 font-medium rounded-xl hover:bg-zinc-700 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={clearWeek}
                    className="flex-1 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-500 transition-colors"
                  >
                    Vider
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Context Menu */}
        <AnimatePresence>
          {showContextMenu && (
            <MobileContextMenu
              event={showContextMenu.event}
              onEdit={() => openEditSheet(showContextMenu.event)}
              onDuplicate={() => duplicateEvent(showContextMenu.event)}
              onDelete={() => deleteEvent(showContextMenu.event.id)}
              onClose={() => setShowContextMenu(null)}
            />
          )}
        </AnimatePresence>

        {/* Add Sheet */}
        <AddEventSheet
          isOpen={showAddSheet}
          onClose={() => setShowAddSheet(false)}
          onAdd={(text) => {
            const parsed = parseQuickAdd(text);
            addEvent(parsed);
            setShowAddSheet(false);
          }}
          onTemplate={applyTemplate}
          templates={QUICK_TEMPLATES}
        />

        {/* Edit Sheet */}
        <EditEventSheet
          isOpen={showEditSheet}
          event={editingEvent}
          onClose={() => {
            setShowEditSheet(false);
            setEditingEvent(null);
          }}
          onSave={(updated) => {
            if (updated.id) {
              updateEvent(updated.id, updated);
            } else {
              addEvent(updated);
            }
            setShowEditSheet(false);
            setEditingEvent(null);
          }}
          onDelete={(id) => {
            deleteEvent(id);
            setShowEditSheet(false);
            setEditingEvent(null);
          }}
          categories={CATEGORIES}
        />

        {/* AI Week Generator Sheet */}
        <WeekGeneratorSheet
          isOpen={showWeekGenerator}
          onClose={() => setShowWeekGenerator(false)}
          onGenerate={handleAIGenerate}
          userProfile={profile}
          onUpdateProfile={saveProfile}
          isGenerating={isAIGenerating}
          weekDates={weekDates}
          onOpenProfile={() => {
            setShowWeekGenerator(false);
            setShowProfileSetup(true);
          }}
        />

        {/* Profile Setup Sheet */}
        <ProfileSetupSheet
          isOpen={showProfileSetup}
          onClose={() => setShowProfileSetup(false)}
          profile={profile}
          onSaveProfile={(updates) => {
            saveProfile(updates);
            setShowProfileSetup(false);
            // Ouvrir le générateur après avoir configuré le profil
            setTimeout(() => setShowWeekGenerator(true), 300);
          }}
        />
      </div>
    );
  }

  // ============ DESKTOP VIEW ============
  return (
    <>
      <DesktopWeekView
        events={events}
        weekDates={weekDates}
        weekStart={weekStart}
        setWeekStart={setWeekStart}
        currentTime={currentTime}
        onEventsChange={onEventsChange}
        onDeleteEvent={onDeleteEvent}
        addEvent={addEvent}
        updateEvent={updateEvent}
        deleteEvent={deleteEvent}
        duplicateEvent={duplicateEvent}
        parseQuickAdd={parseQuickAdd}
        selectedDate={selectedDate}
        goToday={goToday}
        // AI Generation props
        onOpenGenerator={handleOpenGenerator}
        isAIGenerating={isAIGenerating}
        // Clear week props
        weekEventCount={weekEventCount}
        onClearWeek={clearWeek}
        showClearConfirm={showClearConfirm}
        setShowClearConfirm={setShowClearConfirm}
      />

      {/* AI Week Generator Sheet - also for desktop */}
      <WeekGeneratorSheet
        isOpen={showWeekGenerator}
        onClose={() => setShowWeekGenerator(false)}
        onGenerate={handleAIGenerate}
        userProfile={profile}
        onUpdateProfile={saveProfile}
        isGenerating={isAIGenerating}
        weekDates={weekDates}
        onOpenProfile={() => {
          setShowWeekGenerator(false);
          setShowProfileSetup(true);
        }}
      />

      {/* Profile Setup Sheet - also for desktop */}
      <ProfileSetupSheet
        isOpen={showProfileSetup}
        onClose={() => setShowProfileSetup(false)}
        profile={profile}
        onSaveProfile={(updates) => {
          saveProfile(updates);
          setShowProfileSetup(false);
          setTimeout(() => setShowWeekGenerator(true), 300);
        }}
      />
    </>
  );
});

/**
 * Mobile Context Menu (Bottom Sheet style)
 */
const MobileContextMenu = memo(function MobileContextMenu({
  event,
  onEdit,
  onDuplicate,
  onDelete,
  onClose,
}) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        className="fixed bottom-0 left-0 right-0 bg-zinc-900 rounded-t-2xl p-4 pb-8 z-50"
      >
        <div className="w-10 h-1 bg-zinc-700 rounded-full mx-auto mb-4" />
        <h3 className="font-semibold text-white mb-4">{event.title}</h3>

        <div className="space-y-2">
          <button
            onClick={onEdit}
            className="w-full flex items-center gap-3 p-4 bg-zinc-800 rounded-xl text-zinc-200"
          >
            <Edit3 className="w-5 h-5" />
            Modifier
          </button>
          <button
            onClick={onDuplicate}
            className="w-full flex items-center gap-3 p-4 bg-zinc-800 rounded-xl text-zinc-200"
          >
            <Copy className="w-5 h-5" />
            Dupliquer
          </button>
          <button
            onClick={onDelete}
            className="w-full flex items-center gap-3 p-4 bg-red-500/10 rounded-xl text-red-400"
          >
            <Trash2 className="w-5 h-5" />
            Supprimer
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full mt-4 p-4 bg-zinc-800 rounded-xl text-zinc-400 font-medium"
        >
          Annuler
        </button>
      </motion.div>
    </>
  );
});

/**
 * Add Event Sheet
 */
const AddEventSheet = memo(function AddEventSheet({
  isOpen,
  onClose,
  onAdd,
  onTemplate,
  templates,
}) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      setText('');
    }
  }, [isOpen]);

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Ajouter un événement">
      <div className="px-4 pb-6">
        {/* Quick input */}
        <div className="relative mb-4">
          <Zap className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && text.trim()) {
                onAdd(text);
              }
            }}
            placeholder="Ex: Réunion 14h, Sport 18h 1h..."
            className="w-full pl-12 pr-4 py-4 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {text.trim() && (
          <button
            onClick={() => onAdd(text)}
            className="w-full mb-4 py-4 bg-blue-600 text-white font-semibold rounded-xl"
          >
            Ajouter "{text}"
          </button>
        )}

        {/* Templates */}
        <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3">
          Templates rapides
        </p>
        <div className="grid grid-cols-2 gap-2">
          {templates.map((tpl) => (
            <button
              key={tpl.name}
              onClick={() => onTemplate(tpl)}
              className="flex items-center gap-2 p-3 bg-zinc-800 rounded-xl text-left"
            >
              <span className="text-xl">{tpl.icon}</span>
              <div>
                <span className="text-sm font-medium text-zinc-200">{tpl.name}</span>
                <span className="text-xs text-zinc-500 block">{tpl.duration} min</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </BottomSheet>
  );
});

/**
 * Edit Event Sheet with subtasks
 */
const EditEventSheet = memo(function EditEventSheet({
  isOpen,
  event,
  onClose,
  onSave,
  onDelete,
  categories,
}) {
  const [title, setTitle] = useState('');
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('10:00');
  const [category, setCategory] = useState('personal');
  const [subTasks, setSubTasks] = useState([]);
  const [newSubTask, setNewSubTask] = useState('');

  useEffect(() => {
    if (event) {
      setTitle(event.title || '');
      setStart(event.start || '09:00');
      setEnd(event.end || '10:00');
      setCategory(event.category || 'personal');
      setSubTasks(event.subTasks || []);
    }
  }, [event]);

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({
      ...event,
      title: title.trim(),
      start,
      end,
      category,
      subTasks,
    });
  };

  const addSubTask = () => {
    if (!newSubTask.trim()) return;
    setSubTasks([...subTasks, { id: uid(), text: newSubTask.trim(), completed: false }]);
    setNewSubTask('');
  };

  const toggleSubTask = (id) => {
    setSubTasks(subTasks.map(st =>
      st.id === id ? { ...st, completed: !st.completed } : st
    ));
  };

  const deleteSubTask = (id) => {
    setSubTasks(subTasks.filter(st => st.id !== id));
  };

  if (!event) return null;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={event.id ? 'Modifier' : 'Nouveau'} size="large">
      <div className="px-4 pb-6 space-y-5">
        {/* Title */}
        <div>
          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
            Titre
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Nom de l'événement..."
            className="w-full mt-2 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Time */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
              Début
            </label>
            <input
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full mt-2 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex items-end pb-3 text-zinc-600">→</div>
          <div className="flex-1">
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
              Fin
            </label>
            <input
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full mt-2 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
            Catégorie
          </label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className={`p-3 rounded-xl text-sm font-medium transition-all ${
                  category === cat.id
                    ? 'ring-2 ring-white scale-105'
                    : 'opacity-60'
                }`}
                style={{ backgroundColor: cat.bg, color: cat.color }}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Subtasks */}
        <div>
          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
            Sous-tâches
          </label>

          <div className="mt-2 space-y-2">
            {subTasks.map((st) => (
              <div
                key={st.id}
                className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-xl"
              >
                <button
                  onClick={() => toggleSubTask(st.id)}
                  className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${
                    st.completed
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-zinc-600'
                  }`}
                >
                  {st.completed && <Check className="w-4 h-4" />}
                </button>
                <span className={`flex-1 text-sm ${st.completed ? 'line-through text-zinc-500' : 'text-zinc-200'}`}>
                  {st.text}
                </span>
                <button
                  onClick={() => deleteSubTask(st.id)}
                  className="p-1 text-zinc-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2 mt-2">
            <input
              type="text"
              value={newSubTask}
              onChange={(e) => setNewSubTask(e.target.value)}
              placeholder="Ajouter une étape..."
              className="flex-1 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && addSubTask()}
            />
            <button
              onClick={addSubTask}
              className="px-4 py-3 bg-zinc-700 rounded-xl text-white"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          {event.id && (
            <button
              onClick={() => onDelete(event.id)}
              className="p-4 bg-red-500/10 text-red-400 rounded-xl"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 py-4 bg-zinc-800 text-zinc-300 font-medium rounded-xl"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-4 bg-blue-600 text-white font-semibold rounded-xl"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </BottomSheet>
  );
});

/**
 * Desktop Week View (Original grid layout)
 */
const DesktopWeekView = memo(function DesktopWeekView({
  events,
  weekDates,
  weekStart,
  setWeekStart,
  currentTime,
  addEvent,
  updateEvent,
  deleteEvent,
  duplicateEvent,
  parseQuickAdd,
  goToday,
  // AI Generation props
  onOpenGenerator,
  isAIGenerating = false,
  // Clear week props
  weekEventCount = 0,
  onClearWeek,
  showClearConfirm,
  setShowClearConfirm,
}) {
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddText, setQuickAddText] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const quickInputRef = useRef(null);

  // Drag-to-create state
  const [dragCreate, setDragCreate] = useState(null); // { date, startMinutes, currentMinutes }
  const dragColumnRef = useRef(null);

  // Drag-to-move state (pour déplacer un événement existant)
  const [dragMove, setDragMove] = useState(null); // { event, startY, originalStart, currentStart, duration, columnElement }
  const [isDraggingMove, setIsDraggingMove] = useState(false);

  const weekLabel = useMemo(() => {
    const start = weekDates[0];
    const end = weekDates[6];
    return `${start.getDate()} ${MONTHS[start.getMonth()]} — ${end.getDate()} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`;
  }, [weekDates]);

  const changeWeek = useCallback((dir) => {
    setWeekStart(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + dir * 7);
      return next;
    });
  }, [setWeekStart]);

  const handleQuickAdd = useCallback(() => {
    if (!quickAddText.trim()) return;
    const parsed = parseQuickAdd(quickAddText);
    addEvent(parsed);
    setQuickAddText('');
    setShowQuickAdd(false);
  }, [quickAddText, parseQuickAdd, addEvent]);

  const applyTemplate = useCallback((template) => {
    const now = new Date();
    const start = minutesToTime(roundMinutes(now.getHours() * 60 + now.getMinutes() + 15));
    const end = minutesToTime(timeToMinutes(start) + template.duration);
    addEvent({
      title: template.name,
      date: formatDateKey(now),
      start,
      end,
      category: template.category,
      subTasks: [],
    });
  }, [addEvent]);

  const openEditModal = useCallback((eventId) => {
    const event = events.find(e => e.id === eventId);
    if (event) {
      setEditModal({ event: { ...event }, isNew: false });
    }
    setContextMenu(null);
  }, [events]);

  // Drag-to-create handlers
  const getMinutesFromY = useCallback((clientY, columnElement) => {
    if (!columnElement) return HOUR_START * 60;
    const rect = columnElement.getBoundingClientRect();
    const relativeY = clientY - rect.top;
    const minutes = (relativeY / HOUR_HEIGHT) * 60 + HOUR_START * 60;
    return roundMinutes(Math.max(HOUR_START * 60, Math.min(minutes, HOUR_END * 60)), 15);
  }, []);

  const handleColumnMouseDown = useCallback((e, date, columnElement) => {
    // Ctrl+clic force la création même sur un événement existant
    const forceCreate = e.ctrlKey || e.metaKey;

    // Ignore if clicking on an event (sauf si Ctrl+clic)
    if (!forceCreate && e.target.closest('.event-card')) return;
    e.preventDefault();
    e.stopPropagation(); // Empêcher le drag-to-move de l'événement

    const startMinutes = getMinutesFromY(e.clientY, columnElement);
    dragColumnRef.current = columnElement;

    setDragCreate({
      date: formatDateKey(date),
      startMinutes,
      currentMinutes: startMinutes + 30,
    });
  }, [getMinutesFromY]);

  const handleMouseMove = useCallback((e) => {
    // Drag-to-create
    if (dragCreate && dragColumnRef.current) {
      const currentMinutes = getMinutesFromY(e.clientY, dragColumnRef.current);
      setDragCreate(prev => ({
        ...prev,
        currentMinutes,
      }));
      return;
    }

    // Drag-to-move
    if (dragMove && dragMove.columnElement) {
      const currentMinutes = getMinutesFromY(e.clientY, dragMove.columnElement);
      const deltaMinutes = currentMinutes - (dragMove.originalStart + dragMove.duration / 2);
      const newStart = roundMinutes(dragMove.originalStart + deltaMinutes, 15);
      const clampedStart = Math.max(HOUR_START * 60, Math.min(newStart, (HOUR_END * 60) - dragMove.duration));

      setDragMove(prev => ({
        ...prev,
        currentStart: clampedStart,
      }));
      setIsDraggingMove(true);
    }
  }, [dragCreate, dragMove, getMinutesFromY]);

  const handleMouseUp = useCallback(() => {
    // Drag-to-create
    if (dragCreate) {
      const startMin = Math.min(dragCreate.startMinutes, dragCreate.currentMinutes);
      const endMin = Math.max(dragCreate.startMinutes, dragCreate.currentMinutes);

      // Open modal with new event
      setEditModal({
        event: {
          title: '',
          date: dragCreate.date,
          start: minutesToTime(startMin),
          end: minutesToTime(Math.max(endMin, startMin + 30)),
          category: 'personal',
          subTasks: [],
        },
        isNew: true,
      });

      setDragCreate(null);
      dragColumnRef.current = null;
      return;
    }

    // Drag-to-move
    if (dragMove && isDraggingMove) {
      const newStart = minutesToTime(dragMove.currentStart);
      const newEnd = minutesToTime(dragMove.currentStart + dragMove.duration);

      updateEvent(dragMove.event.id, {
        start: newStart,
        end: newEnd,
      });
    }

    setDragMove(null);
    setIsDraggingMove(false);
  }, [dragCreate, dragMove, isDraggingMove, updateEvent]);

  // Handler pour commencer le drag d'un événement existant
  const handleEventMouseDown = useCallback((e, event, columnElement) => {
    e.preventDefault();
    e.stopPropagation();

    const startMin = timeToMinutes(event.start);
    const endMin = timeToMinutes(event.end);

    setDragMove({
      event,
      startY: e.clientY,
      originalStart: startMin,
      currentStart: startMin,
      duration: endMin - startMin,
      columnElement,
    });
  }, []);

  // Global mouse listeners for drag
  useEffect(() => {
    if (dragCreate || dragMove) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragCreate, dragMove, handleMouseMove, handleMouseUp]);

  // Close context menu on outside click
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Top Bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex-shrink-0">
        <div className="flex items-center gap-1">
          <button
            onClick={() => changeWeek(-1)}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <button
            onClick={() => changeWeek(1)}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <span className="text-sm font-semibold text-zinc-200">{weekLabel}</span>

        <button
          onClick={goToday}
          className="px-3 py-1.5 text-xs font-medium text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
        >
          Aujourd'hui
        </button>

        {/* AI Generator Button */}
        <button
          onClick={onOpenGenerator}
          disabled={isAIGenerating}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
            ${isAIGenerating
              ? 'bg-purple-600/20 text-purple-400 cursor-wait'
              : 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20 text-purple-400 hover:text-purple-300'
            }`}
          title="Générer ma semaine avec l'IA"
        >
          {isAIGenerating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          <span className="hidden lg:inline">Générer ma semaine</span>
          <span className="lg:hidden">IA</span>
        </button>

        {/* Clear Week Button */}
        {weekEventCount > 0 && (
          <button
            onClick={() => setShowClearConfirm?.(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-all"
            title="Vider la semaine"
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden lg:inline">Vider ({weekEventCount})</span>
          </button>
        )}

        <div className="flex-1" />

        <button
          onClick={() => {
            setShowQuickAdd(true);
            setTimeout(() => quickInputRef.current?.focus(), 50);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouveau
        </button>
      </div>

      {/* Quick Add Bar */}
      <AnimatePresence>
        {showQuickAdd && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-b border-zinc-800 bg-zinc-900/80 overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 relative">
                <Zap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  ref={quickInputRef}
                  type="text"
                  value={quickAddText}
                  onChange={(e) => setQuickAddText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleQuickAdd();
                    if (e.key === 'Escape') setShowQuickAdd(false);
                  }}
                  placeholder='Ajout rapide : "Réunion mardi 14h-16h", "Sport demain 18h 1h"...'
                  className="w-full pl-10 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex gap-2">
                {QUICK_TEMPLATES.slice(0, 4).map((tpl) => (
                  <button
                    key={tpl.name}
                    onClick={() => applyTemplate(tpl)}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-full text-xs text-zinc-300 transition-colors whitespace-nowrap"
                  >
                    {tpl.icon} {tpl.name}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowQuickAdd(false)}
                className="p-2 hover:bg-zinc-800 rounded-lg"
              >
                <X className="w-4 h-4 text-zinc-500" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto">
        <div
          className="grid min-w-[800px]"
          style={{ gridTemplateColumns: '50px repeat(7, 1fr)' }}
        >
          {/* Time Gutter */}
          <div className="bg-zinc-900 border-r border-zinc-800 sticky left-0 z-10">
            <div className="h-12 border-b border-zinc-800" />
            {Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => (
              <div
                key={i}
                className="flex items-start justify-end pr-2 text-[10px] font-mono text-zinc-500"
                style={{ height: HOUR_HEIGHT, transform: 'translateY(-6px)' }}
              >
                {String(HOUR_START + i).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {/* Day Columns */}
          {weekDates.map((date) => {
            const dateKey = formatDateKey(date);
            const dayEvents = events.filter(e => e.date === dateKey);
            const isTodayDate = isToday(date);

            return (
              <div
                key={dateKey}
                className={`border-r border-zinc-800 last:border-r-0 ${isTodayDate ? 'bg-blue-950/20' : ''}`}
              >
                {/* Header */}
                <div className="sticky top-0 z-20 bg-zinc-900 border-b border-zinc-800 py-2 text-center">
                  <div className={`text-[10px] font-bold uppercase tracking-wider ${isTodayDate ? 'text-blue-400' : 'text-zinc-500'}`}>
                    {DAYS_SHORT[date.getDay()]}
                  </div>
                  <div
                    className={`w-8 h-8 mx-auto mt-1 flex items-center justify-center text-lg font-bold rounded-full ${
                      isTodayDate ? 'bg-blue-500 text-white' : 'text-zinc-200'
                    }`}
                  >
                    {date.getDate()}
                  </div>
                </div>

                {/* Body */}
                <div
                  data-column={dateKey}
                  className="relative select-none"
                  style={{ cursor: dragCreate || dragMove ? 'ns-resize' : 'crosshair' }}
                  onMouseDown={(e) => handleColumnMouseDown(e, date, e.currentTarget)}
                >
                  {/* Hour slots */}
                  {Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => (
                    <div
                      key={i}
                      className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
                      style={{ height: HOUR_HEIGHT }}
                    />
                  ))}

                  {/* Drag-to-create preview */}
                  {dragCreate && dragCreate.date === dateKey && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute left-1 right-1 bg-blue-500/30 border-2 border-dashed border-blue-500 rounded-lg z-40 flex flex-col items-center justify-center pointer-events-none"
                      style={{
                        top: ((Math.min(dragCreate.startMinutes, dragCreate.currentMinutes) - HOUR_START * 60) / 60) * HOUR_HEIGHT,
                        height: Math.max(
                          ((Math.abs(dragCreate.currentMinutes - dragCreate.startMinutes)) / 60) * HOUR_HEIGHT,
                          20
                        ),
                      }}
                    >
                      <span className="text-blue-400 font-bold text-xs">
                        {minutesToTime(Math.min(dragCreate.startMinutes, dragCreate.currentMinutes))}
                      </span>
                      <span className="text-blue-300 text-[10px]">
                        — {minutesToTime(Math.max(dragCreate.startMinutes, dragCreate.currentMinutes))}
                      </span>
                    </motion.div>
                  )}

                  {/* Now line */}
                  {isTodayDate && (() => {
                    const mins = (currentTime.getHours() - HOUR_START) * 60 + currentTime.getMinutes();
                    if (mins < 0 || mins > (HOUR_END - HOUR_START + 1) * 60) return null;
                    const top = (mins / 60) * HOUR_HEIGHT;
                    return (
                      <div
                        className="absolute left-0 right-0 h-0.5 bg-red-500 z-30 pointer-events-none"
                        style={{ top }}
                      >
                        <div className="absolute -left-1 -top-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
                      </div>
                    );
                  })()}

                  {/* Drag-to-move: indicateur de position originale */}
                  {isDraggingMove && dragMove?.event?.date === dateKey && (
                    <div
                      className="absolute left-1 right-1 border-2 border-dashed border-zinc-500 rounded-md z-5 pointer-events-none"
                      style={{
                        top: ((dragMove.originalStart - HOUR_START * 60) / 60) * HOUR_HEIGHT,
                        height: (dragMove.duration / 60) * HOUR_HEIGHT,
                        backgroundColor: 'rgba(100, 100, 100, 0.1)',
                      }}
                    />
                  )}

                  {/* Events */}
                  {dayEvents.map((event) => {
                    const category = CATEGORIES.find(c => c.id === event.category) || CATEGORIES[3];
                    const startMin = timeToMinutes(event.start);
                    const endMin = timeToMinutes(event.end);
                    const duration = endMin - startMin;
                    const height = Math.max((duration / 60) * HOUR_HEIGHT, 20);

                    // Si cet événement est en train d'être déplacé
                    const isBeingDragged = isDraggingMove && dragMove?.event?.id === event.id;
                    const displayTop = isBeingDragged
                      ? ((dragMove.currentStart - HOUR_START * 60) / 60) * HOUR_HEIGHT
                      : ((startMin - HOUR_START * 60) / 60) * HOUR_HEIGHT;

                    return (
                      <motion.div
                        key={event.id}
                        className={`event-card absolute left-1 right-1 rounded-md px-2 py-1 overflow-hidden border-l-3 ${
                          selectedEvent === event.id ? 'ring-2 ring-blue-500 z-30' : 'z-10'
                        } ${isBeingDragged ? 'shadow-xl shadow-blue-500/30 z-50 cursor-grabbing' : 'cursor-grab'}`}
                        style={{
                          top: displayTop,
                          height,
                          backgroundColor: category.bg,
                          borderLeftColor: category.color,
                          borderLeftWidth: 3,
                          opacity: isBeingDragged ? 0.9 : 1,
                          transform: isBeingDragged ? 'scale(1.02)' : undefined,
                        }}
                        onClick={() => {
                          if (!isDraggingMove) {
                            setSelectedEvent(event.id);
                          }
                        }}
                        onDoubleClick={() => {
                          if (!isDraggingMove) {
                            openEditModal(event.id);
                          }
                        }}
                        onMouseDown={(e) => {
                          // Démarrer le drag-to-move
                          const columnElement = e.currentTarget.closest('[data-column]') || e.currentTarget.parentElement;
                          handleEventMouseDown(e, event, columnElement);
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          if (!isDraggingMove) {
                            setContextMenu({ x: e.clientX, y: e.clientY, eventId: event.id });
                            setSelectedEvent(event.id);
                          }
                        }}
                        whileHover={isDraggingMove ? {} : { boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
                        animate={isBeingDragged ? { scale: 1.02 } : { scale: 1 }}
                        transition={{ duration: 0.1 }}
                      >
                        <div className="text-xs font-semibold truncate" style={{ color: category.color }}>
                          {event.title}
                        </div>
                        {height > 30 && (
                          <div className="text-[9px] font-mono opacity-60" style={{ color: category.color }}>
                            {isBeingDragged
                              ? `${minutesToTime(dragMove.currentStart)} – ${minutesToTime(dragMove.currentStart + dragMove.duration)}`
                              : `${event.start} – ${event.end}`
                            }
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed bg-zinc-900 border border-zinc-700 rounded-xl p-1 min-w-[160px] shadow-xl z-50"
            style={{
              left: Math.min(contextMenu.x, window.innerWidth - 180),
              top: Math.min(contextMenu.y, window.innerHeight - 200),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => openEditModal(contextMenu.eventId)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 rounded-lg"
            >
              <Edit3 className="w-4 h-4" /> Modifier
            </button>
            <button
              onClick={() => {
                const event = events.find(e => e.id === contextMenu.eventId);
                if (event) duplicateEvent(event);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 rounded-lg"
            >
              <Copy className="w-4 h-4" /> Dupliquer
            </button>
            <div className="h-px bg-zinc-700 my-1" />
            <button
              onClick={() => deleteEvent(contextMenu.eventId)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-950/50 rounded-lg"
            >
              <Trash2 className="w-4 h-4" /> Supprimer
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {editModal && (
          <DesktopEditModal
            event={editModal.event}
            isNew={editModal.isNew}
            onSave={(updated) => {
              if (editModal.isNew) {
                addEvent(updated);
              } else {
                updateEvent(updated.id, updated);
              }
              setEditModal(null);
            }}
            onDelete={() => {
              deleteEvent(editModal.event.id);
              setEditModal(null);
            }}
            onClose={() => setEditModal(null)}
            weekDates={weekDates}
          />
        )}
      </AnimatePresence>

      {/* Clear Week Confirmation Modal - Desktop */}
      <AnimatePresence>
        {showClearConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              onClick={() => setShowClearConfirm?.(false)}
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-zinc-900 border border-zinc-700 rounded-2xl p-6 z-50 w-full max-w-md text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                <Trash2 className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Vider la semaine ?</h3>
              <p className="text-sm text-zinc-400 mb-6">
                Cette action supprimera <span className="text-red-400 font-bold">{weekEventCount} événement(s)</span> de la semaine en cours.
                <br />
                <span className="text-xs text-zinc-500">Cette action peut être annulée avec Ctrl+Z</span>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearConfirm?.(false)}
                  className="flex-1 py-3 bg-zinc-800 text-zinc-300 font-medium rounded-xl hover:bg-zinc-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    onClearWeek?.();
                  }}
                  className="flex-1 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-500 transition-colors"
                >
                  Vider la semaine
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
});

/**
 * Desktop Edit Modal
 */
const DesktopEditModal = memo(function DesktopEditModal({
  event,
  isNew,
  onSave,
  onDelete,
  onClose,
  weekDates,
}) {
  const [title, setTitle] = useState(event.title || '');
  const [date, setDate] = useState(event.date || '');
  const [start, setStart] = useState(event.start || '09:00');
  const [end, setEnd] = useState(event.end || '10:00');
  const [category, setCategory] = useState(event.category || 'personal');
  const [subTasks, setSubTasks] = useState(event.subTasks || []);
  const [newSubTask, setNewSubTask] = useState('');

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({ ...event, title: title.trim(), date, start, end, category, subTasks });
  };

  const addSubTask = () => {
    if (!newSubTask.trim()) return;
    setSubTasks([...subTasks, { id: uid(), text: newSubTask.trim(), completed: false }]);
    setNewSubTask('');
  };

  const toggleSubTask = (id) => {
    setSubTasks(subTasks.map(st => st.id === id ? { ...st, completed: !st.completed } : st));
  };

  const deleteSubTask = (id) => {
    setSubTasks(subTasks.filter(st => st.id !== id));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-zinc-900 border border-zinc-700 rounded-2xl p-5 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-white mb-4">
          {isNew ? 'Nouvel événement' : 'Modifier'}
        </h2>

        {/* Title */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1">
            Titre
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Nom de l'événement..."
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Date */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1">
            Jour
          </label>
          <select
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:border-blue-500"
          >
            {weekDates.map((d) => (
              <option key={formatDateKey(d)} value={formatDateKey(d)}>
                {DAYS_SHORT[d.getDay()]} {d.getDate()} {isToday(d) ? '★' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Time */}
        <div className="mb-4 flex gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1">
              Début
            </label>
            <input
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex items-end pb-2 text-zinc-500">→</div>
          <div className="flex-1">
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1">
              Fin
            </label>
            <input
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Category */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
            Catégorie
          </label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  category === cat.id ? 'ring-2 ring-white scale-105' : 'opacity-60 hover:opacity-100'
                }`}
                style={{ backgroundColor: cat.bg, color: cat.color }}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Sub-tasks */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
            Sous-tâches
          </label>
          <div className="space-y-2 mb-3">
            {subTasks.map((st) => (
              <div key={st.id} className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded-lg group">
                <button
                  onClick={() => toggleSubTask(st.id)}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    st.completed ? 'bg-green-500 border-green-500 text-white' : 'border-zinc-600 hover:border-green-500'
                  }`}
                >
                  {st.completed && <Check className="w-3 h-3" />}
                </button>
                <span className={`flex-1 text-sm ${st.completed ? 'line-through text-zinc-500' : 'text-zinc-200'}`}>
                  {st.text}
                </span>
                <button
                  onClick={() => deleteSubTask(st.id)}
                  className="p-1 text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newSubTask}
              onChange={(e) => setNewSubTask(e.target.value)}
              placeholder="Ajouter une sous-tâche..."
              className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && addSubTask()}
            />
            <button onClick={addSubTask} className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-6">
          {!isNew && (
            <button onClick={onDelete} className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-medium rounded-lg">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium rounded-lg">
            Annuler
          </button>
          <button onClick={handleSave} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg">
            Enregistrer
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
});

export default WeeklyAgenda;
